import { useContext, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { maskWholeNumber, pluralize } from '@givebutter/util';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { createBulkAction } from 'api/bulkActions';
import { useContactTypeContext } from 'assets/js/pages/contacts/ContactTypeContext';
import { ContactsViewContext } from 'assets/js/pages/contacts/ContactsViewContext';
import * as DropdownMenu from 'components/dropdown-menu';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import * as Form from 'components/form';
import { TableContext } from 'components/table/TableContext';
import * as Tooltip from 'components/tooltip';
import { BulkActionType } from 'constants/bulkActions';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface DeleteContactConfirmationValues {
    confirmation: string;
}
interface DeleteSelectedContactsProps extends Omit<AlertDialog.AlertDialogRootProps, 'open'> {}

const DeleteContactsDialog = ({ onOpenChange, children, ...rest }: DeleteSelectedContactsProps) => {
    const contactType = useContactTypeContext();
    const filterKey = contactType === ContactType.Individual ? 'contacts' : 'companies';
    const [isOpen, setIsOpen] = useState(false);
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { error, success } = useToast();
    const { table, searchTerm } = useContext(TableContext);
    const { archivedInput } = useContext(ContactsViewContext);
    const isArchived = archivedInput === 'archived';
    const tableState = table.getState();
    const selectedRows = table.getSelectedRowModel().flatRows;
    const { filters, currentGroup } = useFiltersByKey(filterKey);
    const isAllSelected = tableState.isAllRecordsSelected;
    const selectedRowIds = Object.keys(tableState.rowSelection).map(Number);
    const contactIds: number[] = isAllSelected ? [] : selectedRowIds;
    const rowCount = isAllSelected ? table.getRowCount() : contactIds.length;
    const deletableFilter = currentGroup?.filters.find(filter => filter.serverKey === 'deletable');
    const canBulkDelete =
        Array.isArray(deletableFilter?.value) && deletableFilter?.value.includes('deletable');
    const canDeleteSelected = selectedRows.every(({ original }) => !original.cant_delete);
    const canDelete = isAllSelected ? canBulkDelete : canDeleteSelected;

    const methods = Form.useForm<DeleteContactConfirmationValues>({
        defaultValues: {
            confirmation: '',
        },
    });
    const {
        path,
        handleSubmit,
        control,
        reset,
        formState: { isValid },
    } = methods;

    const { mutate: bulkDelete, isPending } = useMutation({
        mutationFn: () => {
            return createBulkAction(accountId, {
                type: BulkActionType.DELETE_CONTACTS,
                filters: isAllSelected
                    ? {
                          query: searchTerm,
                          type: contactType,
                          archiveOnly: isArchived,
                          ...filters,
                      }
                    : undefined,
                data: { ids: isAllSelected ? undefined : contactIds },
            });
        },
        onSuccess: () => {
            success('Delete contacts bulk action queued successfully');
            table.toggleAllRowsSelected?.(false);
            setIsOpen(false);
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.bulkActions(accountId) });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unknown error occurred');
        },
    });

    return (
        <AlertDialog.Root
            type="error"
            open={isOpen}
            onOpenChange={isOpen => {
                setIsOpen(isOpen);
                onOpenChange?.(isOpen);
                reset();
            }}
            {...rest}
        >
            <AlertDialog.Trigger asChild disabled={!canDelete} onClick={() => setIsOpen(true)}>
                {!isAllSelected && selectedRows.some(({ original }) => original.cant_delete) ? (
                    <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                            <DropdownMenu.Item disabled onSelect={e => e.preventDefault()}>
                                Delete contacts
                            </DropdownMenu.Item>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content inverse>
                                <Tooltip.Arrow inverse />
                                <p>
                                    The following contacts can't be deleted:
                                    <ul className="list-inside list-disc">
                                        {selectedRows
                                            .filter(item => item.original.cant_delete)
                                            .map(({ original }) => (
                                                <li key={original.id}>
                                                    {filterKey === 'contacts' ? (
                                                        <>
                                                            {original.first_name}{' '}
                                                            {original.last_name}
                                                        </>
                                                    ) : (
                                                        <>{original.company_name}</>
                                                    )}
                                                </li>
                                            ))}
                                    </ul>
                                </p>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                ) : (
                    <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                        Delete {filterKey}
                    </DropdownMenu.Item>
                )}
            </AlertDialog.Trigger>
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <Form.Provider {...methods}>
                    <form onSubmit={handleSubmit(() => bulkDelete())}>
                        <AlertDialog.Content className="max-w-lg">
                            <AlertDialog.Header>
                                <AlertDialog.Icon customIcon={faTrash} />
                                <AlertDialog.Close />
                            </AlertDialog.Header>
                            <AlertDialog.Title>Delete contacts?</AlertDialog.Title>
                            <AlertDialog.Body className="gap-4">
                                {isPending && <Loader />}
                                <AlertDialog.Description>
                                    <AlertNotification.Root type="warning">
                                        <AlertNotification.Icon />
                                        <AlertNotification.Content>
                                            <AlertNotification.Title>
                                                You are about to delete {maskWholeNumber(rowCount)}{' '}
                                                {pluralize(rowCount, 'contact')}
                                                {isAllSelected ? ' across multiple pages.' : '.'}
                                            </AlertNotification.Title>
                                            <AlertNotification.Description>
                                                Deleting a contact is an irreversible action and
                                                cannot be undone. Contacts with transaction
                                                histories will not be deleted.
                                            </AlertNotification.Description>
                                        </AlertNotification.Content>
                                    </AlertNotification.Root>
                                </AlertDialog.Description>
                                <Form.Container>
                                    <Form.Label name={path('confirmation')}>
                                        Type <strong>DELETE</strong> to confirm
                                    </Form.Label>
                                    <Form.TextInputRoot
                                        name="confirmation"
                                        control={control}
                                        rules={{
                                            validate: value =>
                                                value === 'DELETE' || 'Must enter DELETE',
                                        }}
                                    >
                                        <Form.TextInputForm placeholder="DELETE" />
                                    </Form.TextInputRoot>
                                    <Form.Error name={path('confirmation')} />
                                </Form.Container>
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel className="flex-1">Cancel</AlertDialog.Cancel>
                                <Form.SubmitButton
                                    variant="solid"
                                    className="flex-1"
                                    color="error"
                                    disabled={!isValid}
                                    isSubmitting={isPending}
                                >
                                    Delete
                                </Form.SubmitButton>
                            </AlertDialog.Footer>
                        </AlertDialog.Content>
                    </form>
                </Form.Provider>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

export { DeleteContactsDialog };
